/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    AuthConfigOptions,
    AuthModel,
    ConfirmAccountPayload,
    LoginPayload,
    OpenIdStateModel,
    PasswordComplexity,
    PrepareToLoginPayload,
    RequestedRoute,
    ResetForgottenPasswordPayload,
    ResetPasswordPayload,
    TokenRefreshResponse,
    UserInfoResponse,
} from './auth.model';

export class LoadAuthConfig {
    static readonly type = '[Auth] Load Auth config from server';
}

export class SetAuthConfigOptions {
    static readonly type = '[Auth] Set Config Options';

    constructor(public payload: AuthConfigOptions) {}
}

export class BeginPasswordLogin {
    static readonly type = '[Auth] Begin Login';

    constructor(public payload: LoginPayload) {}
}

export class LoginSuccessful {
    static readonly type = '[Auth] Login Successful';

    constructor(public payload: Partial<AuthModel>) {}
}

export class LoginFailed {
    static readonly type = '[Auth] Login Failed';

    constructor(public payload: { message: string }) {}
}

export class PrepareToLogin {
    static readonly type = '[Auth] Prepare to Login';

    constructor(public payload: PrepareToLoginPayload) {}
}

export class BeginLogout {
    static readonly type = '[Auth] Begin Logout';

    constructor(public redirect: boolean) {}
}

export class LogoutBackend {
    static readonly type = '[Auth] Logout backend';
}

export class LoggedOut {
    static readonly type = '[Auth] Logged out';

    constructor(public redirect: boolean) {}
}

export class RefreshAccessToken {
    static readonly type = '[Auth] Refresh the access token';

    constructor(public token: string) {}
}

export class BeginTokenRefresh {
    static readonly type = '[Auth] Begin Token Refresh';
}

export class TokenRefreshSuccessful {
    static readonly type = '[Auth] Token Refresh Successful';

    constructor(public payload: TokenRefreshResponse) {}
}

export class TokenRefreshFailed {
    static readonly type = '[Auth] Token Refresh Failed';

    constructor(public reason: string) {}
}

export class RequestUserInfo {
    static readonly type = '[Auth] Request User Info';
}

export class SetUserInfo {
    static readonly type = '[Auth] Set User Info';

    constructor(public payload: UserInfoResponse) {}
}

export class DisclaimerSeen {
    static readonly type = '[Auth] Disclaimer Seen';
}

export class SetRequestedRoute {
    static readonly type = '[Auth] Set Requested Route';

    constructor(public requestedRoute: RequestedRoute) {}
}

export class RedirectToLogin {
    static readonly type = '[Auth] Redirect to login';

    constructor(public route: RequestedRoute) {}
}

export class NavigateToRequestedRoute {
    static readonly type = '[Auth] Navigate to requested route after authentication';

    constructor(public fallbackRoute?: RequestedRoute) {}
}

export class InitForgotPasswordRequest {
    static readonly type = '[Auth] Init Forgot Password Request';

    constructor(public identifier: string) {}
}

export class AcceptedForgotPasswordRequest {
    static readonly type = '[Auth] Forgot Password Request Accepted';
}

export class CheckResetKey {
    static readonly type = '[Auth] Check Password Reset Key';

    constructor(public resetKey: string) {}
}

export class ResetForgottenPassword {
    static readonly type = '[Auth] Perform Forgotten Password Reset';

    constructor(public payload: ResetForgottenPasswordPayload) {}
}

export class ResetForgottenPasswordSuccess {
    static readonly type = '[Auth] Reset Forgotten Password Success';
}

export class ResetForgottenPasswordFailure {
    static readonly type = '[Auth] Reset Forgotten Password Failure';

    constructor(public message: string) {}
}

export class ResetPassword {
    static readonly type = '[Auth] Reset password logged-in user';

    constructor(public payload: ResetPasswordPayload) {}
}

export class ResetPasswordSuccess {
    static readonly type = '[Auth] Reset password success';
}


export class ResetPasswordFailed {
    static readonly type = '[Auth] Reset password failed';

    constructor(public message: string) {}
}

export class BeginInitialAuthentication {
    static readonly type = '[Auth] Begin initial authentication';
}

export class EndInitialAuthentication {
    static readonly type = '[Auth] End initial authentication';
}

export class BeginOAuthInitialAuthentication {
    static readonly type = '[Auth] Begin OAuth initial authentication';
}

export class BeginPasswordInitialAuthentication {
    static readonly type = '[Auth] Begin Password initial authentication';
}


export class UpdatePasswordComplexityUI {
    static readonly type = '[Auth] Update Password Complexity UI';

    constructor(public complexity: PasswordComplexity) {}
}

export class BeginForceResetPassword {
    static readonly type = '[Auth] Begin Force Reset Password';
}

export class EndForceResetPassword {
    static readonly type = '[Auth] End Force Reset Password';
}

export class CheckConfirmationKey {
    static readonly type = '[Auth] Check Confirmation Key';

    constructor(public confirmationKey: string) {}
}

export class CheckConfirmationKeySuccess {
    static readonly type = '[Auth] Check Confirmation Key Success';
}

export class CheckConfirmationKeyFailed {
    static readonly type = '[Auth] Check Confirmation Key Failed';
}

export class BeginConfirmAccount {
    static readonly type = '[Auth] Begin Confirm Account';

    constructor(public payload: ConfirmAccountPayload) {}
}

export class ConfirmAccountSuccess {
    static readonly type = '[Auth] Confirm Account Success';
}

export class ConfirmAccountFailed {
    static readonly type = '[Auth] Confirm Account Failed';

    constructor(public message: string) {}
}

// OAuth

export class OpenIdLoginSuccessful {
    static readonly type = '[Auth] Successful login via OpenId';
}

export class ConfigureOpenId {
    static readonly type = '[Auth] Set OpenId Connect configuration';

    constructor(public config: Partial<OpenIdStateModel>) {}
}

export class InitOpenIdSignUp {
    static readonly type = '[Auth] Init Azure Sign Up';
}



